import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { Check, X, Award, DollarSign, Lock } from 'lucide-react'

const STARTER_INSTRUCTOR_LIMIT = 7
const STANDARD_INSTRUCTOR_LIMIT = 14

export default function AdminInstructorApprovals() {
  const { user, profile } = useAuth()
  const [pending, setPending] = useState([])
  const [approved, setApproved] = useState([])
  const [loading, setLoading] = useState(true)
  const [payRates, setPayRates] = useState({})
  const [saving, setSaving] = useState({})
  const [tab, setTab] = useState('pending')
  const [error, setError] = useState('')

  useEffect(() => {
    if (user) loadInstructors()
  }, [user])

  const loadInstructors = async () => {
    setLoading(true)
    setError('')

    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch('/api/admin/instructors', {
      headers: { Authorization: `Bearer ${session?.access_token}` },
    })
    const json = await res.json()

    if (!res.ok) {
      setError(json.error || 'Error loading instructors')
      setLoading(false)
      return
    }

    if (!json.instructors || json.instructors.length === 0) {
      setPending([])
      setApproved([])
      setLoading(false)
      return
    }

    const all = json.instructors

    setPending(all.filter(i => !i.approved))
    setApproved(all.filter(i => i.approved))

    const rates = {}
    all.forEach(i => { rates[i.id] = i.hourly_rate || '' })
    setPayRates(rates)
    setLoading(false)
  }

  const handleApprove = async (instructor) => {
    const rate = parseFloat(payRates[instructor.id])
    if (!rate || rate <= 0) {
      alert('Please enter a pay rate before approving.')
      return
    }
    setSaving(s => ({ ...s, [instructor.id]: true }))
    const { error } = await supabase
      .from('instructors')
      .update({ approved: true, hourly_rate: rate, is_active: true })
      .eq('id', instructor.id)

    if (!error) {
      setPending(p => p.filter(i => i.id !== instructor.id))
      setApproved(a => [...a, { ...instructor, approved: true, hourly_rate: rate }])
    } else {
      alert('Error approving: ' + error.message)
    }
    setSaving(s => ({ ...s, [instructor.id]: false }))
  }

  const handleDeny = async (instructorId) => {
    if (!window.confirm('Deny and remove this instructor application?')) return
    setSaving(s => ({ ...s, [instructorId]: true }))
    await supabase.from('instructors').delete().eq('id', instructorId)
    setPending(p => p.filter(i => i.id !== instructorId))
    setSaving(s => ({ ...s, [instructorId]: false }))
  }

  const handleUpdateRate = async (instructorId) => {
    const rate = parseFloat(payRates[instructorId])
    if (!rate) return
    setSaving(s => ({ ...s, [instructorId]: true }))
    await supabase.from('instructors').update({ hourly_rate: rate }).eq('id', instructorId)
    setApproved(a => a.map(i => i.id === instructorId ? { ...i, hourly_rate: rate } : i))
    setSaving(s => ({ ...s, [instructorId]: false }))
  }

  const initials = name => (name || '??').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

  const InstructorCard = ({ instructor, showApprove }) => (
    <div style={{
      background: 'white', border: '1px solid rgba(11,30,61,0.08)',
      borderRadius: 14, padding: '1.25rem',
      boxShadow: '0 2px 8px rgba(11,30,61,0.05)',
      display: 'flex', flexDirection: 'column', gap: '1rem',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%', background: '#0B1E3D',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontWeight: 700, fontSize: '0.875rem', flexShrink: 0,
        }}>
          {initials(instructor.profile?.full_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}>
            {instructor.profile?.full_name || 'Unknown Instructor'}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: 2 }}>
            {instructor.profile?.email || '—'}
          </div>
        </div>
        <span style={{
          padding: '4px 12px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
          background: instructor.approved ? 'rgba(14,170,164,0.12)' : 'rgba(212,168,75,0.15)',
          color: instructor.approved ? '#0EAAA4' : '#a07a1a',
        }}>
          {instructor.approved ? 'Approved' : 'Pending'}
        </span>
      </div>

      {/* Specialties */}
      {instructor.specialties?.length > 0 && (
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Specialties</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {instructor.specialties.map(s => (
              <span key={s} style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', background: 'rgba(11,30,61,0.07)', textTransform: 'capitalize' }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications */}
      {instructor.certifications?.length > 0 && (
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Certifications</div>
          {instructor.certifications.map(c => (
            <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--text-mid)' }}>
              <Award size={12} color="#D4A84B" /> {c}
            </div>
          ))}
        </div>
      )}

      {/* Bio */}
      {instructor.bio && (
        <p style={{ fontSize: '0.825rem', color: 'var(--text-mid)', fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>
          "{instructor.bio}"
        </p>
      )}

      {/* Pay rate + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: '0.875rem', borderTop: '1px solid rgba(11,30,61,0.07)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <DollarSign size={14} color="#0EAAA4" />
          <input
            type="number" min={0} max={999} step={5}
            placeholder="Set pay rate..."
            value={payRates[instructor.id] ?? ''}
            onChange={e => setPayRates(r => ({ ...r, [instructor.id]: e.target.value }))}
            style={{
              width: 120, padding: '7px 10px', borderRadius: 8,
              border: '1.5px solid rgba(11,30,61,0.15)',
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem',
            }}
          />
          <span style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>/hr</span>
        </div>

        {showApprove ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleDeny(instructor.id)} disabled={saving[instructor.id]}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(232,97,74,0.3)', background: 'rgba(232,97,74,0.08)', color: '#E8614A', cursor: 'pointer', fontSize: '0.825rem', fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>
              <X size={13} /> Deny
            </button>
            {atInstructorLimit ? (
              <div title="Starter plan is limited to 7 approved instructors" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(212,168,75,0.3)', background: 'rgba(212,168,75,0.08)', color: '#a07a1a', fontSize: '0.825rem', fontWeight: 600, cursor: 'default' }}>
                <Lock size={13} /> Limit Reached
              </div>
            ) : (
              <button onClick={() => handleApprove(instructor)} disabled={saving[instructor.id] || !payRates[instructor.id]}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: 'none', background: payRates[instructor.id] ? '#0EAAA4' : 'rgba(14,170,164,0.3)', color: 'white', cursor: payRates[instructor.id] ? 'pointer' : 'not-allowed', fontSize: '0.825rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif' }}>
                <Check size={13} /> {saving[instructor.id] ? 'Saving...' : 'Approve'}
              </button>
            )}
          </div>
        ) : (
          <button onClick={() => handleUpdateRate(instructor.id)} disabled={saving[instructor.id]}
            style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(11,30,61,0.15)', background: 'transparent', cursor: 'pointer', fontSize: '0.825rem', fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>
            {saving[instructor.id] ? 'Saving...' : 'Update Rate'}
          </button>
        )}
      </div>
    </div>
  )

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '4rem' }}>
      <div className="spinner" style={{ margin: '0 auto', width: 36, height: 36, borderWidth: 3 }} />
      <p style={{ marginTop: 12, color: 'var(--text-light)' }}>Loading instructors...</p>
    </div>
  )

  if (error) return (
    <div style={{ padding: '2rem', background: 'rgba(232,97,74,0.08)', border: '1px solid rgba(232,97,74,0.2)', borderRadius: 12, color: '#E8614A', lineHeight: 1.6 }}>
      <strong>Error:</strong> {error}
    </div>
  )

  const instructorLimit = profile?.plan_tier === 'starter' ? STARTER_INSTRUCTOR_LIMIT
    : profile?.plan_tier === 'standard' ? STANDARD_INSTRUCTOR_LIMIT
    : Infinity
  const atInstructorLimit = instructorLimit < Infinity && approved.length >= instructorLimit

  return (
    <div>
      {atInstructorLimit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: '1.25rem', background: 'rgba(212,168,75,0.1)', border: '1px solid rgba(212,168,75,0.3)', borderRadius: 12 }}>
          <Lock size={16} color="#a07a1a" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#a07a1a' }}>Instructor limit reached </span>
            <span style={{ fontSize: '0.8rem', color: '#a07a1a' }}>— Your plan supports up to {instructorLimit} approved instructors. Upgrade for more.</span>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem' }}>
        {[
          { key: 'pending', label: `Pending${pending.length > 0 ? ` (${pending.length})` : ''}` },
          { key: 'approved', label: `Approved (${approved.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', fontWeight: 600,
            background: tab === t.key ? '#0B1E3D' : 'rgba(11,30,61,0.07)',
            color: tab === t.key ? 'white' : 'var(--text-light)',
            transition: 'all 0.2s',
          }}>{t.label}</button>
        ))}
        <button onClick={loadInstructors} style={{
          marginLeft: 'auto', padding: '8px 16px', borderRadius: 8,
          border: '1px solid rgba(11,30,61,0.15)', background: 'transparent',
          color: 'var(--text-light)', cursor: 'pointer', fontSize: '0.8rem',
          fontFamily: 'DM Sans, sans-serif',
        }}>↻ Refresh</button>
      </div>

      {tab === 'pending' && (
        pending.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-light)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <p style={{ fontWeight: 600 }}>No pending applications.</p>
            <p style={{ fontSize: '0.875rem', marginTop: 8, opacity: 0.7 }}>When an instructor signs up and picks your school, they'll appear here.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {pending.map(inst => <InstructorCard key={inst.id} instructor={inst} showApprove={true} />)}
          </div>
        )
      )}

      {tab === 'approved' && (
        approved.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-light)' }}>
            <p>No approved instructors yet.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {approved.map(inst => <InstructorCard key={inst.id} instructor={inst} showApprove={false} />)}
          </div>
        )
      )}
    </div>
  )
}
